'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'

/** A finite CSS timeline; no polling, frame loop, or model requests. */
export function ModelMotionStudy({
  modelId,
  name,
  children
}: {
  modelId: string
  name: string
  children: ReactNode
}) {
  const ref = useRef<HTMLElement>(null)
  const [motionAllowed, setMotionAllowed] = useState(false)
  const [visible, setVisible] = useState(false)
  const [paused, setPaused] = useState(false)
  const [finished, setFinished] = useState(false)
  const [take, setTake] = useState(0)

  useEffect(() => {
    // Preserve the complete server-rendered illustration when motion APIs are absent.
    if (
      typeof window.matchMedia !== 'function' ||
      typeof IntersectionObserver === 'undefined'
    )
      return
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setMotionAllowed(!preference.matches)
    update()
    preference.addEventListener('change', update)
    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting && !document.hidden)
      },
      { threshold: 0.25 }
    )
    if (ref.current) observer.observe(ref.current)
    const visibility = () => {
      if (document.hidden) setVisible(false)
      else if (ref.current) {
        const rect = ref.current.getBoundingClientRect()
        setVisible(rect.bottom > 0 && rect.top < window.innerHeight)
      }
    }
    document.addEventListener('visibilitychange', visibility)
    return () => {
      preference.removeEventListener('change', update)
      observer.disconnect()
      document.removeEventListener('visibilitychange', visibility)
    }
  }, [])

  const state =
    !motionAllowed || finished
      ? 'still'
      : visible && !paused
        ? 'playing'
        : 'paused'
  return (
    <figure
      ref={ref}
      className={`nv-workbench nv-${modelId}`}
      data-motion={state}
      aria-label={`Illustrative ${name} workflow`}
    >
      <div className="nv-toolbar">
        <span>
          <i aria-hidden="true" /> NIN / WORKFLOW STUDY
        </span>
        <span>Illustrative</span>
      </div>
      <div className="nv-take" key={take}>
        {children}
        <div
          className="nv-motion-progress"
          aria-hidden="true"
          onAnimationEnd={event => {
            if (event.target === event.currentTarget) setFinished(true)
          }}
        />
      </div>
      <figcaption>
        <span>Planned output</span>
        <span>Human review →</span>
      </figcaption>
      {motionAllowed && (
        <div className="nv-motion-controls">
          <span>Illustrative motion · no live inference</span>
          <div>
            {!finished && (
              <button type="button" onClick={() => setPaused(value => !value)}>
                {paused ? 'Resume' : 'Pause'}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setTake(value => value + 1)
                setFinished(false)
                setPaused(false)
              }}
            >
              Replay <span aria-hidden="true">↻</span>
            </button>
          </div>
        </div>
      )}
    </figure>
  )
}
